import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

// Extend Window interface for PayPal
declare global {
  interface Window {
    paypal?: any;
  }
}

// Input validation schema
const donationSchema = z.object({
  name: z.string()
    .trim()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters")
    .regex(/^[a-zA-Z\s\-']+$/, "Name can only contain letters, spaces, hyphens, and apostrophes"),
  email: z.string()
    .trim()
    .email("Invalid email address")
    .max(255, "Email must be less than 255 characters"),
  amount: z.number()
    .min(1, "Minimum donation is $1")
    .max(100000, "Maximum donation is $100,000")
});

export const DonationForm = ({ defaultCategory = "church" }: { defaultCategory?: string }) => {
  const [searchParams] = useSearchParams();
  const [amount, setAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState(defaultCategory);
  const [showSuccess, setShowSuccess] = useState(false);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  useEffect(() => {
    // Check for URL parameters
    const typeParam = searchParams.get("type");
    const memberIdParam = searchParams.get("member_id");
    
    if (typeParam === "tithe") {
      setCategory("tithe");
    } else if (typeParam === "monthly_dues" || typeParam === "monthly_rental") {
      setCategory("monthly-rental");
    }
    
    if (memberIdParam) {
      setMemberId(memberIdParam);
      loadMemberData(memberIdParam);
    }
  }, [searchParams]);

  const loadMemberData = async (memberIdParam: string) => {
    try {
      const { data, error } = await supabase
        .from("members")
        .select("full_name, email")
        .eq("id", memberIdParam)
        .single();

      if (error) throw error;
      
      if (data) {
        setName(data.full_name);
        setEmail(data.email || "");
      }
    } catch (error) {
      console.error("Error loading member data:", error);
    }
  };

  // Validate individual field
  const validateField = (field: 'name' | 'email' | 'amount', value: string | number) => {
    try {
      if (field === 'amount') {
        donationSchema.shape[field].parse(typeof value === 'string' ? parseFloat(value) : value);
      } else {
        donationSchema.shape[field].parse(value);
      }
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        setValidationErrors(prev => ({
          ...prev,
          [field]: error.errors[0]?.message || 'Invalid input'
        }));
      }
    }
  };

  // Sanitize text input by removing potentially dangerous characters
  const sanitizeInput = (input: string): string => {
    return input.trim().replace(/[<>]/g, '');
  };

  // Load PayPal SDK
  useEffect(() => {
    const loadPayPalScript = async () => {
      try {
        console.log("[PayPal] Starting to load PayPal SDK...");
        
        // Check if script already loaded
        if (window.paypal) {
          console.log("[PayPal] Script already loaded");
          setPaypalLoaded(true);
          return;
        }

        console.log("[PayPal] Fetching configuration from backend...");
        
        // Get PayPal Client ID from backend
        const { data, error } = await supabase.functions.invoke("get-paypal-config");

        console.log("[PayPal] Configuration response:", { data, error });

        if (error || !data?.clientId) {
          console.error("[PayPal] Failed to get Client ID:", error);
          toast({
            title: "Configuration Error",
            description: "PayPal is not configured. Please contact support.",
            variant: "destructive",
          });
          return;
        }

        console.log("[PayPal] Client ID received, loading SDK...");

        const script = document.createElement("script");
        script.src = `https://www.paypal.com/sdk/js?client-id=${data.clientId}&currency=USD&components=buttons&disable-funding=paylater,venmo`;
        script.async = true;
        script.onload = () => {
          console.log("[PayPal] SDK loaded successfully");
          setPaypalLoaded(true);
        };
        script.onerror = () => {
          console.error("[PayPal] Failed to load PayPal SDK script");
          toast({
            title: "Payment Error",
            description: "Failed to load payment system. Please try again later.",
            variant: "destructive",
          });
        };
        document.body.appendChild(script);
      } catch (error) {
        console.error("[PayPal] Exception during initialization:", error);
        toast({
          title: "Configuration Error",
          description: "Failed to initialize payment system. Please try again later.",
          variant: "destructive",
        });
      }
    };

    loadPayPalScript();
  }, []);


  // Render PayPal buttons when ready
  useEffect(() => {
    const donationAmount = parseFloat(amount);
    
    if (!paypalLoaded || !window.paypal || !amount || isNaN(donationAmount) || donationAmount < 1 || !name.trim() || !email.trim()) {
      return;
    }

    const container = document.getElementById("paypal-button-container");
    if (!container) return;
    
    // Clear existing buttons before rendering new ones
    container.innerHTML = '';

    // Calculate processing fee: 3.99% + $0.49
    const processingFee = (donationAmount * 0.0399) + 0.49;
    const totalAmount = donationAmount + processingFee;

    try {
      window.paypal.Buttons({
        createOrder: async () => {
          try {
            setIsProcessing(true);
            
            // Recalculate with current amount
            const currentAmount = parseFloat(amount);
            const currentFee = (currentAmount * 0.0399) + 0.49;
            const currentTotal = currentAmount + currentFee;
            
            // Validate all inputs before processing
            const validationResult = donationSchema.safeParse({
              name: name.trim(),
              email: email.trim(),
              amount: currentAmount
            });
            
            if (!validationResult.success) {
              const errors: Record<string, string> = {};
              validationResult.error.errors.forEach(err => {
                if (err.path[0]) {
                  errors[err.path[0] as string] = err.message;
                }
              });
              setValidationErrors(errors);
              toast({
                title: "Validation Error",
                description: "Please correct the errors in the form.",
                variant: "destructive",
              });
              setIsProcessing(false);
              throw new Error("Validation failed");
            }
            
            // Sanitize inputs before sending to backend
            const sanitizedName = sanitizeInput(name.trim());
            const sanitizedEmail = email.trim().toLowerCase();
            
            const { data, error } = await supabase.functions.invoke(
              "create-paypal-order",
              {
                body: {
                  amount: currentTotal,
                  name: sanitizedName,
                  email: sanitizedEmail,
                  category,
                  member_id: memberId,
                },
              }
            );

            if (error) throw error;
            return data.orderID;
          } catch (error) {
            console.error("Error creating order:", error);
            toast({
              title: "Error",
              description: "Failed to create payment order. Please try again.",
              variant: "destructive",
            });
            setIsProcessing(false);
            throw error;
          }
        },
        onApprove: async (data: any) => {
          try {
            const { data: captureData, error } = await supabase.functions.invoke(
              "capture-paypal-payment",
              {
                body: { orderID: data.orderID },
              }
            );

            if (error) throw error;

            const finalAmount = parseFloat(amount);
            toast({
              title: "Thank you for your donation!",
              description: `Your donation of $${finalAmount.toFixed(2)} has been processed successfully.`,
            });
            
            setTimeout(() => {
              setShowSuccess(true);
              setAmount("");
              setName("");
              setEmail("");
              setCategory(defaultCategory);
              setMemberId(null);
            }, 500);
          } catch (error) {
            console.error("Error capturing payment:", error);
            toast({
              title: "Payment Error",
              description: "Failed to complete payment. Please contact us for assistance.",
              variant: "destructive",
            });
          } finally {
            setIsProcessing(false);
          }
        },
        onError: (err: any) => {
          console.error("PayPal error:", err);
          toast({
            title: "Payment Error",
            description: "An error occurred during payment. Please try again.",
            variant: "destructive",
          });
          setIsProcessing(false);
        },
        onCancel: () => {
          toast({
            title: "Payment Cancelled",
            description: "Your donation was cancelled.",
          });
          setIsProcessing(false);
        },
        style: {
          layout: 'vertical',
          color: 'gold',
          shape: 'rect',
          label: 'donate',
          height: 45
        }
      }).render('#paypal-button-container');
    } catch (error) {
      console.error("Error rendering PayPal buttons:", error);
    }
  }, [paypalLoaded, amount, name, email, category, memberId]);

  const donationAmount = parseFloat(amount);
  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const isFormValid = name.trim() && 
                      email.trim() && 
                      !isNaN(donationAmount) && 
                      donationAmount >= 1 && 
                      donationAmount <= 100000 &&
                      !hasValidationErrors;

  if (showSuccess) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-semibold mb-2">Thank You!</h3>
          <p className="text-muted-foreground mb-6">
            Your generous donation has been received. You should receive a receipt via email shortly.
          </p>
          <Button onClick={() => setShowSuccess(false)}>Make Another Donation</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h3 className="text-xl font-semibold mb-4">Make a Donation</h3>
      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            type="text"
            value={name}
            onChange={(e) => {
              const value = e.target.value;
              setName(value);
              if (value.trim()) {
                validateField('name', value);
              }
            }}
            onBlur={() => name.trim() && validateField('name', name)}
            placeholder="Your name"
            required
            disabled={isProcessing}
            className={validationErrors.name ? "border-destructive" : ""}
          />
          {validationErrors.name && (
            <p className="text-sm text-destructive mt-1">{validationErrors.name}</p>
          )}
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              const value = e.target.value;
              setEmail(value);
              if (value.trim()) {
                validateField('email', value);
              }
            }}
            onBlur={() => email.trim() && validateField('email', email)}
            placeholder="your@email.com"
            required
            disabled={isProcessing}
            className={validationErrors.email ? "border-destructive" : ""}
          />
          {validationErrors.email && (
            <p className="text-sm text-destructive mt-1">{validationErrors.email}</p>
          )}
        </div>
        <div>
          <Label htmlFor="category">Donation Category</Label>
          <Select value={category} onValueChange={(value) => {
            setCategory(value);
          }} disabled={isProcessing}>
            <SelectTrigger>
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="guatemala">Guatemala Mission</SelectItem>
              <SelectItem value="mexico">Mexico Mission</SelectItem>
              <SelectItem value="church">Church Donation</SelectItem>
              <SelectItem value="bible-school">Bible School</SelectItem>
              <SelectItem value="tithe">Tithe</SelectItem>
              <SelectItem value="monthly-rental">Monthly Rental Dues</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="amount">Donation Amount ($)</Label>
          <Input
            id="amount"
            type="number"
            min="1"
            max="100000"
            step="0.01"
            value={amount}
            onChange={(e) => {
              const value = e.target.value;
              setAmount(value);
              if (value) {
                const numValue = parseFloat(value);
                if (!isNaN(numValue)) {
                  validateField('amount', numValue);
                }
              }
            }}
            onBlur={() => {
              if (amount) {
                const numValue = parseFloat(amount);
                if (!isNaN(numValue)) {
                  validateField('amount', numValue);
                }
              }
            }}
            placeholder="50.00"
            required
            disabled={isProcessing}
            className={validationErrors.amount ? "border-destructive" : ""}
          />
          {validationErrors.amount && (
            <p className="text-sm text-destructive mt-1">{validationErrors.amount}</p>
          )}
          {amount && donationAmount > 0 && (() => {
            const processingFee = (donationAmount * 0.0399) + 0.49;
            const totalAmount = donationAmount + processingFee;
            
            return (
              <div className="mt-3 p-3 bg-card border rounded-lg space-y-2 text-sm">
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Donation Amount:</span>
                    <span className="font-medium">${donationAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Processing Fee (3.99% + $0.49):</span>
                    <span>${processingFee.toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between font-semibold">
                      <span>Total Charge:</span>
                      <span className="text-primary">${totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground italic pt-2">
                  100% of your ${donationAmount.toFixed(2)} donation goes to {category === 'tithe' ? 'your tithe' : category === 'monthly-rental' ? 'monthly dues' : `the ${category}`}. The processing fee covers payment processing costs.
                </p>
              </div>
            );
          })()}
        </div>

        {isFormValid && paypalLoaded ? (
          <div id="paypal-button-container" className="mt-4"></div>
        ) : (
          <Button 
            className="w-full" 
            disabled={!isFormValid || !paypalLoaded}
          >
            {!paypalLoaded ? "Loading Payment System..." : "Complete Form to Continue"}
          </Button>
        )}
        
        {isProcessing && (
          <div className="text-center text-sm text-muted-foreground">
            Processing your donation...
          </div>
        )}
      </div>
    </Card>
  );
};
